/* eslint-disable jsx-a11y/no-autofocus */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import type React from 'react';
import { useTranslation } from 'react-i18next';

export const LifecycleAction = {
  Archive: 'Archive',
  Restore: 'Restore',
  Delete: 'Delete',
};
export type LifecycleActionType = (typeof LifecycleAction)[keyof typeof LifecycleAction]; // "Archive" | "Restore" | "Delete"

type LifecycleActionDialogProps = {
  entityLabel: string;
  isOpen: boolean;
  lifecycleAction: LifecycleActionType;
  details?: string;
  onClose: () => void;
  onSubmit: () => void;
};

const LifecycleActionDialog: React.FC<LifecycleActionDialogProps> = ({
  entityLabel,
  isOpen,
  lifecycleAction,
  details,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation('translation');

  let title: string = '';
  let warning: string = '';
  let buttonLabel: string = '';

  switch (lifecycleAction) {
    case LifecycleAction.Archive:
      title = t(($) => $.ideaManagement.general.archiveEntity, { entityLabel });
      warning = t(($) => $.ideaManagement.general.areYouSureYouWantToArchiveThisEntity, {
        entityLabel,
      });
      buttonLabel = t(($) => $.ideaManagement.general.archive);
      break;
    case LifecycleAction.Restore:
      title = t(($) => $.ideaManagement.general.restoreEntity, { entityLabel });
      warning = t(($) => $.ideaManagement.general.areYouSureYouWantToRestoreThisEntity, {
        entityLabel,
      });
      buttonLabel = t(($) => $.ideaManagement.general.restore);
      break;
    case LifecycleAction.Delete:
      title = t(($) => $.ideaManagement.general.deleteEntity, { entityLabel });
      warning = t(($) => $.ideaManagement.general.areYouSureYouWantToDeleteThisEntity, {
        entityLabel,
      });
      buttonLabel = t(($) => $.ideaManagement.general.delete);
      break;
  }

  const handleCloseDialog = () => {
    onClose();
  };

  const handleSubmit = () => {
    onSubmit();
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleCloseDialog}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{ sx: { width: '40%', maxHeight: '90vh', padding: '1rem' } }}
    >
      <DialogTitle id="alert-dialog-title" data-testid="fim3-lifecycle-dialog-title">
        {title}
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          id="alert-dialog-description"
          data-testid="fim3-lifecycle-dialog-description"
          component="div"
        >
          {warning}
          {lifecycleAction === LifecycleAction.Delete && (
            <span>
              <p />
              <p>{t(($) => $.ideaManagement.general.thisActionCannotBeUndone)}</p>
            </span>
          )}
        </DialogContentText>
        {details && (
          <p
            className="text-sm leading-normal text-text-warning mt-2"
            data-testid="fim3-lifecycle-dialog-details"
          >
            {details}
          </p>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          data-testid="fim3-lifecycle-dialog-cancel"
          variant="outlined"
          onClick={handleCloseDialog}
        >
          {t(($) => $.common.cancel)}
        </Button>
        <Button
          data-testid="fim3-lifecycle-dialog-submit"
          onClick={handleSubmit}
          variant="contained"
          size="small"
          autoFocus
        >
          {buttonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LifecycleActionDialog;
